import { Button, Col, Input, InputNumber, Row, Select } from 'antd'
import React, { useEffect, useState } from 'react'
import {
  SPARTACUS_FILTER_TOOLTIPS,
  SPARTACUS_SEARCH_FILTER_LABEL,
  SPARTACUS_SEARCH_FILTER_PLACEHOLDER,
} from 'src/constants/spartacusTooltips'
import {
  SPARTACUS_ACTIVE_SUPPLIER_AT_GOAL_BUCKETS,
  SPARTACUS_ACTIVE_SUPPLIER_BELOW_GOAL_BUCKETS,
  SPARTACUS_ACTIVE_SUPPLIER_BUCKET_LABELS,
  SPARTACUS_ACTIVE_SUPPLIER_BUCKET_VALUES,
  SPARTACUS_CHANNEL_VALUES,
  SPARTACUS_ORDER_TREND_LABELS,
  SPARTACUS_ORDER_TREND_VALUES,
  SPARTACUS_PLAN_VALUES,
} from 'src/constants/spartacusFilters'
import {
  EMPTY_SPARTACUS_FILTER_FORM,
  applyPresetToFilters,
  buildFormStateFromActiveFilters,
  formStateToApiFilters,
  getPresetSelectionFromFilters,
  hasActiveSpartacusFilters,
} from 'src/utils/spartacusFilters'
import { ColumnHeaderWithTooltip } from 'src/utils/venueProductFieldTooltips'
import styled from 'styled-components/macro'
import { useSpartacusPresets } from './useSpartacusPresets'

const { Option } = Select

function SpartacusFilterLabel({ label, tooltipKey }) {
  const tooltip = SPARTACUS_FILTER_TOOLTIPS[tooltipKey]

  return (
    <FilterLabel>
      {tooltip ? (
        <ColumnHeaderWithTooltip label={label} tooltip={tooltip} />
      ) : (
        label
      )}
    </FilterLabel>
  )
}

function TriStateSelect({ value, onChange }) {
  return (
    <Select
      allowClear
      placeholder='Any'
      value={value}
      onChange={onChange}
      style={{ width: '100%' }}
    >
      <Option value={true}>Yes</Option>
      <Option value={false}>No</Option>
    </Select>
  )
}

function SpartacusFilters({
  filters,
  applyingFilters = false,
  clearingFilters = false,
  onApplyFilters,
  onClearFilters,
}) {
  const { presets, loading: presetsLoading } = useSpartacusPresets()
  const [form, setForm] = useState(() =>
    buildFormStateFromActiveFilters(filters, presets),
  )

  useEffect(() => {
    setForm(buildFormStateFromActiveFilters(filters, presets))
  }, [filters, presets])

  const selectedPreset = getPresetSelectionFromFilters(filters)
  const activePreset = presets.find(preset => preset.id === selectedPreset)

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const handlePresetChange = presetId => {
    if (!presetId) {
      onClearFilters()
      setForm(EMPTY_SPARTACUS_FILTER_FORM)
      return
    }

    const preset = presets.find(item => item.id === presetId)
    const nextForm = buildFormStateFromActiveFilters(
      { preset: presetId, ...(preset?.filters || {}) },
      presets,
    )

    onApplyFilters(applyPresetToFilters(presetId))
    setForm(nextForm)
  }

  const handleApply = () => {
    onApplyFilters(formStateToApiFilters(form))
  }

  const handleClear = () => {
    setForm(EMPTY_SPARTACUS_FILTER_FORM)
    onClearFilters()
  }

  const filtersBusy = applyingFilters || clearingFilters

  return (
    <Panel>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} lg={8}>
          <SpartacusFilterLabel label='Preset view' tooltipKey='preset' />
          <Select
            allowClear
            placeholder='Choose a preset'
            loading={presetsLoading}
            value={selectedPreset}
            onChange={handlePresetChange}
            style={{ width: '100%' }}
          >
            {presets.map(preset => (
              <Option key={preset.id} value={preset.id}>
                {preset.label}
              </Option>
            ))}
          </Select>
          {activePreset?.description ? (
            <PresetDescription>{activePreset.description}</PresetDescription>
          ) : null}
        </Col>

        <Col xs={24} md={12} lg={8}>
          <SpartacusFilterLabel
            label={SPARTACUS_SEARCH_FILTER_LABEL}
            tooltipKey='search'
          />
          <Input
            allowClear
            placeholder={SPARTACUS_SEARCH_FILTER_PLACEHOLDER}
            value={form.search}
            onChange={event => updateField('search', event.target.value)}
            onPressEnter={handleApply}
            data-testid='spartacus-filter-search'
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel label='Plan' tooltipKey='plan' />
          <Select
            allowClear
            placeholder='Any plan'
            value={form.plan}
            onChange={value => updateField('plan', value)}
            style={{ width: '100%' }}
          >
            {SPARTACUS_PLAN_VALUES.map(plan => (
              <Option key={plan} value={plan}>
                {plan}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel label='Channel' tooltipKey='channel' />
          <Select
            allowClear
            placeholder='Any channel'
            value={form.channel}
            onChange={value => updateField('channel', value)}
            style={{ width: '100%' }}
          >
            {SPARTACUS_CHANNEL_VALUES.map(channel => (
              <Option key={channel} value={channel}>
                {channel}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel label='Paid plan' tooltipKey='paidPlan' />
          <TriStateSelect
            value={form.paidPlan}
            onChange={value => updateField('paidPlan', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel label='Order trend' tooltipKey='orderTrend' />
          <Select
            allowClear
            placeholder='Any trend'
            value={form.orderTrend}
            onChange={value => updateField('orderTrend', value)}
            style={{ width: '100%' }}
          >
            {SPARTACUS_ORDER_TREND_VALUES.map(trend => (
              <Option key={trend} value={trend}>
                {SPARTACUS_ORDER_TREND_LABELS[trend]}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel
            label='Min orders'
            tooltipKey='ordersInPeriodMin'
          />
          <InputNumber
            min={0}
            placeholder='0'
            value={form.ordersInPeriodMin}
            onChange={value => updateField('ordersInPeriodMin', value)}
            style={{ width: '100%' }}
          />
        </Col>

        <Col xs={24} md={12} lg={8}>
          <SpartacusFilterLabel
            label='Active suppliers'
            tooltipKey='activeSupplierBuckets'
          />
          <BucketQuickChips>
            <QuickChipButton
              size='small'
              onClick={() =>
                updateField('activeSupplierBuckets', [
                  ...SPARTACUS_ACTIVE_SUPPLIER_BELOW_GOAL_BUCKETS,
                ])
              }
            >
              Below goal (0–4)
            </QuickChipButton>
            <QuickChipButton
              size='small'
              onClick={() =>
                updateField('activeSupplierBuckets', [
                  ...SPARTACUS_ACTIVE_SUPPLIER_AT_GOAL_BUCKETS,
                ])
              }
            >
              At goal (5+)
            </QuickChipButton>
          </BucketQuickChips>
          <Select
            allowClear
            mode='multiple'
            placeholder='Any count'
            value={form.activeSupplierBuckets}
            onChange={value =>
              updateField(
                'activeSupplierBuckets',
                value?.length ? value : undefined,
              )
            }
            style={{ width: '100%' }}
          >
            {SPARTACUS_ACTIVE_SUPPLIER_BUCKET_VALUES.map(bucket => (
              <Option key={bucket} value={bucket}>
                {SPARTACUS_ACTIVE_SUPPLIER_BUCKET_LABELS[bucket]}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel
            label='Xero connected'
            tooltipKey='xeroConnected'
          />
          <TriStateSelect
            value={form.xeroConnected}
            onChange={value => updateField('xeroConnected', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel label='GP using' tooltipKey='gpUsing' />
          <TriStateSelect
            value={form.gpUsing}
            onChange={value => updateField('gpUsing', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel
            label='Has last seen'
            tooltipKey='hasLastSeen'
          />
          <TriStateSelect
            value={form.hasLastSeen}
            onChange={value => updateField('hasLastSeen', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel label='Par levels' tooltipKey='hasParLevels' />
          <TriStateSelect
            value={form.hasParLevels}
            onChange={value => updateField('hasParLevels', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel label='Recipes' tooltipKey='hasRecipes' />
          <TriStateSelect
            value={form.hasRecipes}
            onChange={value => updateField('hasRecipes', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel
            label='Stock takes'
            tooltipKey='hasStockTakes'
          />
          <TriStateSelect
            value={form.hasStockTakes}
            onChange={value => updateField('hasStockTakes', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel
            label='Special purchases'
            tooltipKey='hasSpecialPurchases'
          />
          <TriStateSelect
            value={form.hasSpecialPurchases}
            onChange={value => updateField('hasSpecialPurchases', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel
            label='Restricted suppliers'
            tooltipKey='hasRestrictedSuppliers'
          />
          <TriStateSelect
            value={form.hasRestrictedSuppliers}
            onChange={value => updateField('hasRestrictedSuppliers', value)}
          />
        </Col>

        <Col xs={24} md={8} lg={4}>
          <SpartacusFilterLabel
            label='Feature usage'
            tooltipKey='hasFeatureUsage'
          />
          <TriStateSelect
            value={form.hasFeatureUsage}
            onChange={value => updateField('hasFeatureUsage', value)}
          />
        </Col>

        <Col xs={24} md={6} lg={3}>
          <SpartacusFilterLabel
            label='Upgrade min'
            tooltipKey='upgradeScoreMin'
          />
          <InputNumber
            min={0}
            max={100}
            value={form.upgradeScoreMin}
            onChange={value => updateField('upgradeScoreMin', value)}
            style={{ width: '100%' }}
          />
        </Col>

        <Col xs={24} md={6} lg={3}>
          <SpartacusFilterLabel
            label='Upgrade max'
            tooltipKey='upgradeScoreMax'
          />
          <InputNumber
            min={0}
            max={100}
            value={form.upgradeScoreMax}
            onChange={value => updateField('upgradeScoreMax', value)}
            style={{ width: '100%' }}
          />
        </Col>

        <Col xs={24} md={6} lg={3}>
          <SpartacusFilterLabel label='Risk min' tooltipKey='riskScoreMin' />
          <InputNumber
            min={0}
            max={100}
            value={form.riskScoreMin}
            onChange={value => updateField('riskScoreMin', value)}
            style={{ width: '100%' }}
          />
        </Col>

        <Col xs={24} md={6} lg={3}>
          <SpartacusFilterLabel label='Risk max' tooltipKey='riskScoreMax' />
          <InputNumber
            min={0}
            max={100}
            value={form.riskScoreMax}
            onChange={value => updateField('riskScoreMax', value)}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>

      <Actions>
        <Button
          type='primary'
          loading={applyingFilters}
          disabled={filtersBusy}
          onClick={handleApply}
        >
          {applyingFilters ? 'Applying filters…' : 'Apply filters'}
        </Button>
        <Button
          loading={clearingFilters}
          disabled={filtersBusy || !hasActiveSpartacusFilters(filters)}
          onClick={handleClear}
        >
          {clearingFilters ? 'Clearing filters…' : 'Clear filters'}
        </Button>
      </Actions>
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.xxs};
`

const FilterLabel = styled.div`
  color: ${({ theme }) => theme.colours.darkGrey};
  font-size: 13px;
  margin-bottom: 4px;
  overflow-wrap: normal;
  word-break: keep-all;
`

const PresetDescription = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
  margin: 6px 0 0;
`

const BucketQuickChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
`

const QuickChipButton = styled(Button)`
  font-size: 12px;
  height: auto;
  line-height: 1.3;
  padding: 4px 8px;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
`

export default SpartacusFilters
